import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { isEmpty } from 'lodash';

import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';

export interface Help {
  openChat: boolean;
  isBot: boolean;
  isSFSdkReady: boolean;
}

export interface SalesforceInfo {
  salesforceSrc?: string;
  salesforceScrt?: string;
  salesforceDeploymentCode?: string;
  salesforceDeploymentName?: string;
  salesforceUnauthSdk?: string;
  salesforceAuthSdk?: string;
}

const reducers = {
  setChat: (
    state: Help,
    payload: {
      openChat: boolean;
    }
  ) => ({ ...state, ...payload }),
  setBot: (
    state: Help,
    payload: {
      isBot: boolean;
    }
  ) => ({ ...state, ...payload }),
  setSFSdkReady: (
    state: Help,
    payload: {
      isSFSdkReady: boolean;
    }
  ) => ({ ...state, ...payload })
};

const effects = {
  setOpenChat({ openChat }: { openChat: boolean }, { dispatch }: EffectParams) {
    dispatch.help.setChat({ openChat });
  }
};

const selectors = (slice: Slicer<Help>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getShouldOpenMessenger() {
    return createSelector(
      slice,
      (rootState: RootState) => select.device.isMobile(rootState),
      (rootState: RootState) => select.device.isDesktopEmbedded(rootState),
      (rootState: RootState) => select.device.isMobileEmbedded(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.PSMessenger),
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.PSMessengerConfig),
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.PSMessengerToggle),
      (
        state,
        isMobile,
        isDesktopEmbedded,
        isMobileEmbedded,
        locale,
        brand,
        messengerFlag,
        messengerConfig,
        messengerToggle
      ) => {
        if (messengerToggle) {
          return messengerConfig;
        } else {
          return (
            messengerFlag &&
            !isDesktopEmbedded &&
            !(isMobile && !isMobileEmbedded) &&
            locale === 'en' &&
            (brand === BrandTypes.POKER_STARS || brand === BrandTypes.STARS_CASINO)
          );
        }
      }
    );
  },
  getShouldOpenSFChat() {
    return createSelector(
      slice,
      (rootState: RootState) => select.device.isMobileEmbedded(rootState),
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.SFConfig),
      (_state, isMobileEmbedded, salesforceConfig) => !isMobileEmbedded && salesforceConfig
    );
  },
  getSFChatInfo() {
    return createSelector(
      slice,
      (rootState: RootState) => select.config.get(rootState),
      (rootState: RootState) => select.session.get(rootState),
      (_state, config, session): SalesforceInfo => {
        if (session.idpToken) {
          return {
            salesforceSrc: config.salesforceSrcAuthenticated,
            salesforceScrt: config.salesforceScrt,
            salesforceDeploymentCode: config.salesforceDeploymentCode,
            salesforceDeploymentName: config.salesforceDeploymentNameAuthenticated,
            salesforceAuthSdk: config.salesforceAuthSdk
          };
        } else {
          return {
            salesforceSrc: config.salesforceSrcUnauthenticated,
            salesforceScrt: config.salesforceScrt,
            salesforceDeploymentCode: config.salesforceDeploymentCode,
            salesforceDeploymentName: config.salesforceDeploymentNameUnauthenticated,
            salesforceUnauthSdk: config.salesforceUnauthSdk
          };
        }
      }
    );
  },
  isSFAuthenticated() {
    return createSelector(
      slice,
      (rootState: RootState) => select.session.get(rootState),
      (rootState: RootState) => select.session.isAuthenticated(rootState),
      (_state, { idpToken }, isAuth): boolean => !!idpToken && isAuth
    );
  },
  isSFSdkReady() {
    return createSelector(slice, (state) => state.isSFSdkReady);
  },
  getUserInfo() {
    return createSelector(
      slice,
      (rootState: RootState) => select.session.isClientAuthenticated(rootState),
      (rootState: RootState) => select.account.get(rootState),
      (rootState: RootState) => select.account.getCurrency(rootState),
      (rootState: RootState) => select.session.getWebId(rootState),
      (state, isClientAuthenticated, account, currency, userWebId) => {
        if (!isClientAuthenticated || isEmpty(account)) {
          return null;
        }

        const {
          flags,
          flags2,
          flags3,
          privileges,
          privileges2,
          privileges3,
          licenseId,
          userId,
          country,
          registered
        } = account;

        return {
          userName: userId,
          webId: userWebId,
          license: licenseId ? LicenceId[licenseId] : undefined,
          currency,
          country,
          flags,
          flags2,
          flags3,
          privileges,
          privileges2,
          privileges3,
          registered
        };
      }
    );
  },
  getSiteInfo() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getBrandName(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => select.cms.getSiteName(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.location.getHostname(rootState),
      (state, brandName, ipCountry, siteName, locale, domain) => ({
        brand: brandName,
        locale,
        site: siteName,
        ipCountry,
        domain
      })
    );
  },
  getShouldHideLogin() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.device.isEmbedded(rootState),
      (rootState: RootState) => select.session.isAuthenticated(rootState),
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.PSMessengerIgnoreLogin),
      (state, site, isEmbedded, isAuthenticated, ignoreLogin) =>
        isEmbedded || isAuthenticated || site === Sites.CH || ignoreLogin
    );
  }
});

export interface HelpModel extends ModelConfig<Help> {
  state: Help;
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const help: HelpModel = {
  state: { openChat: false, isBot: false, isSFSdkReady: false },
  reducers,
  effects,
  selectors
};

export default help;
